import { useEffect, useState } from 'react';

import { ActivityStage } from '@showbie-socrative/socrative-utils';

import { useActivityByInstanceId } from '~teacher/hook';

import { useCurrentRoute } from '../CurrentRouteContext';

export enum SelectableTab {
  Launch = 'launch',
  Quizzes = 'quizzes',
  Discover = 'discover',
  Rooms = 'rooms',
  Reports = 'reports',
  LiveResults = 'results/live',
  ProfileBilling = 'profile/billing',
  ProfileInfo = 'profile/info',
  ProfileDemo = 'profile/demo',
  EditQuiz = 'edit-quiz',
  Workspaces = 'workspaces',
}

// If these match, we can just tell which tab to select!
const routes = [
  'launch',
  'quizzes',
  'discover',
  'rooms',
  'reports',
  'edit-quiz',
  'workspaces',
  'profile/info',
  'profile/demo',
  'profile/billing',
  'results/live',
  'results',
] as const;

type Route = typeof routes[number];

const routeMap = new Map<Route, SelectableTab>([
  ['launch', SelectableTab.Launch],
  ['quizzes', SelectableTab.Quizzes],
  ['discover', SelectableTab.Discover],
  ['rooms', SelectableTab.Rooms],
  ['reports', SelectableTab.Reports],
  ['edit-quiz', SelectableTab.EditQuiz],
  ['workspaces', SelectableTab.Workspaces],
  ['profile/info', SelectableTab.ProfileInfo],
  ['profile/demo', SelectableTab.ProfileDemo],
  ['profile/billing', SelectableTab.ProfileBilling],
  ['results/live', SelectableTab.LiveResults],
  ['results', SelectableTab.Reports],
]);

// This is used to check if it's one of the routes that we have simple map for.
function isMappedRoute(route: string): route is Route {
  return routeMap.has(route as Route);
}

// This checks the current route, and maps it to a `SelectedTab` so that we can
// indicate the correct route being active.
export function useMapRouteToSelectedTab() {
  const route = useCurrentRoute();

  const [selectedTab, setSelectedTab] = useState<SelectableTab | null>(null);
  const [activityInstanceId, setActivityInstanceId] = useState(null);
  const activity = useActivityByInstanceId({ activityInstanceId });

  useEffect(() => {
    if (isMappedRoute(route)) {
      setSelectedTab(routeMap.get(route));
    }

    // The `results` route is special, because there is a possibility that
    // it could be a live activity. The only way to know is to check
    // unfortunately, this is a little bit hacky
    const [part1, part2] = route.split('/');

    if (part1 === 'results' && part2 === 'live') {
      setSelectedTab(SelectableTab.LiveResults);
    }

    if (part1 === 'results') {
      setActivityInstanceId(parseInt(part2, 10));

      if (activity?.data?.activity?.activityStage == ActivityStage.InProgress) {
        setSelectedTab(SelectableTab.LiveResults);
      } else {
        setSelectedTab(SelectableTab.Reports);
      }
    }
  }, [activity?.data?.activity?.activityStage, route]);

  return selectedTab;
}
